// Match Lineup — self-contained block rendered directly below the existing
// Detailed Statistics cards in the player-profile Matches tab
// (TP_BUILD_BRIEF_MATCHES_STATS_LINEUP Part B; the design handoff "Match
// Lineup Component.dc.html" is the spec — same card container language, same
// tokens, nothing existing redrawn).
//
// Reusable: the ONLY context inputs are `matchId` (+ optional
// `highlightPlayerId`) — a later club tab embeds it unchanged. Data comes
// from GET /dashboard/matches/<matchId>/lineup/ (persisted provider row,
// field names verbatim — see COORDINATION.md contract).
//
// The lineup fails INDEPENDENTLY of the stats cards above: no lineup (404 /
// empty / error) collapses this block to ONE quiet line — never an empty
// pitch (L5). All breakpoints key on the COMPONENT width (the pane is
// narrower than the viewport): ≥1440 pitch and bench side by side, 1024–1439
// benches below the pitches, <1024 the teams stack, ≤390 per the mobile
// boards (32px nodes, 11px wrapping full names — handoff L10).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { playerService } from "../../../services/api";
import Pitch from "./Pitch";
import PlayerNode from "./PlayerNode";
import {
  splitLineup,
  layoutTeam,
  hasPlacementData,
  boardMetrics,
  collectSubstitutions,
  isSamePerson,
} from "./lineupUtils";
// Part-B-Bench components (parallel build — see COORDINATION.md ownership).
import BenchList from "./BenchList";
import SubstitutionCard from "./SubstitutionCard";
import UnavailableBlock from "./UnavailableBlock";

// Component-width hook: the handoff's width ladder is about the block, not
// the viewport, so we observe the rendered box itself.
const useContainerWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
};

// L12 sheet-vs-popover: the bottom sheet is the MOBILE pattern (handoff
// L12.3 — tap, 390 board); the anchored popover is the desktop pattern
// (L12.1 shows it beside a 620px-wide pitch stage, so a narrow COMPONENT
// is no reason for a sheet). Decide by viewport/pointer, not container
// width: phone-sized window or a touch-only pointer → sheet. Mirrors
// SubstitutionCard's own <768px rule + BenchList's hoverCapable().
const isSheetEnvironment = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  (window.matchMedia("(max-width: 767px)").matches ||
    !window.matchMedia("(hover: hover)").matches);

const sameId = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined &&
  String(a) === String(b);

const MatchLineup = ({ matchId, highlightPlayerId = null }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const width = useContainerWidth(containerRef);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // L12 substitution detail: { side, rows, anchorEl } (bench agent's card)
  const [activeSub, setActiveSub] = useState(null);
  // Hover-close with a grace period so the pointer can travel from the
  // out-arrow into the card (same pattern as BenchList).
  const closeTimer = useRef(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const cancelSubClose = () => clearTimeout(closeTimer.current);
  const scheduleSubClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveSub(null), 150);
  };

  useEffect(() => {
    if (!matchId) {
      setData(null);
      setFailed(false);
      return undefined;
    }
    let isMounted = true;
    setLoading(true);
    setFailed(false);
    setData(null);
    setActiveSub(null);
    playerService
      .getMatchLineup(matchId)
      .then((res) => {
        if (!isMounted) return;
        if (res.success) setData(res.data || null);
        else setFailed(true);
      })
      .catch(() => {
        if (isMounted) setFailed(true);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [matchId]);

  const m = boardMetrics(width || 1024);
  const isSheetMode = isSheetEnvironment(); // L12.3 bottom sheet vs hover card

  const teams = useMemo(() => {
    if (!data) return [];
    return ["home", "away"].map((side) => {
      const players = Array.isArray(data[side]) ? data[side] : [];
      const { xi, bench } = splitLineup(players);
      return {
        side,
        players,
        xi,
        bench,
        slots: layoutTeam(xi, data[`${side}_formation`], side),
        // low-coverage lineups are name-only (no formation, (0,0) sentinel
        // coordinates) — render the XI as a list instead of an empty pitch
        placeable: hasPlacementData(xi, data[`${side}_formation`], side),
        formation: data[`${side}_formation`] || null,
        meta: data[`${side}_team`] || null,
        coach: data.coach ? data.coach[side] : null,
        injuries:
          data.injury && Array.isArray(data.injury[side])
            ? data.injury[side]
            : [],
        // SubstitutionCard/BenchList row shape: pre-formatted minute label +
        // resolved provider player objects (see COORDINATION.md contract).
        // outRef/inRef are the raw provider references (string OR {id, name},
        // see lineupUtils person helpers) — resolve id-first, name fallback.
        subs: collectSubstitutions(players).map((r) => ({
          minute: r.label,
          outPlayer: players.find((p) => isSamePerson(r.outRef, p)) || null,
          inPlayer: players.find((p) => isSamePerson(r.inRef, p)) || null,
        })),
      };
    });
  }, [data]);

  const hasLineup = teams.some((t) => t.xi.length > 0 || t.bench.length > 0);
  const hasRatings = teams.some((t) =>
    t.players.some((p) => Number(p.rating) > 0)
  );
  const preliminary = data ? Number(data.confirmed) === 0 : false;

  const goToPlayer = (p) => {
    if (p && p.platform_player_id) {
      navigate(`/search/player/${p.platform_player_id}`);
    }
  };
  const playerNav = (p) => (p && p.platform_player_id ? goToPlayer : null);

  // L12 anchor on a pitch node's out-arrow: every substitution involving the
  // player (in AND a later off — L12.4) stacks in one card.
  const openSub = (team, player, anchorEl) => {
    const rows = team.subs.filter(
      (r) =>
        (r.outPlayer && r.outPlayer.id === player.id) ||
        (r.inPlayer && r.inPlayer.id === player.id)
    );
    if (rows.length === 0) return;
    setActiveSub({ side: team.side, rows, anchorEl });
  };

  // Nothing selected yet / no match context: render nothing at all.
  if (!matchId) return null;

  const card = (children) => (
    <div
      ref={containerRef}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        padding: m.cardPad,
        display: "flex",
        flexDirection: "column",
        gap: 18,
      }}
    >
      {children}
    </div>
  );

  const title = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          fontSize: m.h1,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: "#0F172A",
        }}
      >
        Line-ups
      </div>
      {preliminary && hasLineup && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748B",
            border: "1px solid #E2E8F0",
            background: "#F8FAFC",
            padding: "3px 8px",
            borderRadius: 999,
          }}
        >
          Preliminary
        </div>
      )}
    </div>
  );

  if (loading) {
    return card(
      <>
        {title}
        <div
          className="animate-pulse"
          style={{
            height: 14,
            width: "38%",
            borderRadius: 5,
            background: "#EEF2F7",
          }}
        />
      </>
    );
  }

  // L5 — no lineup data (independent of the stats cards): ONE quiet line.
  if (failed || !data || !hasLineup) {
    return card(
      <>
        {title}
        <div style={{ fontSize: 14, color: "#64748B", padding: "2px 0 6px" }}>
          No line-up data for this match
        </div>
      </>
    );
  }

  const hasUnavailable = teams.some((t) => t.injuries.length > 0);

  return card(
    <>
      {title}

      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        {/* both teams */}
        <div
          style={{
            display: "flex",
            flexDirection: m.teamsDir,
            gap: m.teamsGap,
          }}
        >
          {teams.map((team) => {
            const clubName = team.meta && team.meta.name;
            const clubLogo = team.meta && team.meta.logo;
            const clubNav =
              team.meta && team.meta.platform_club_id
                ? () => navigate(`/search/club/${team.meta.platform_club_id}`)
                : null;
            // Provider may deliver only a coach id (§0 audit) — a missing
            // name renders nothing, never the raw id. The id (TSCoach) makes
            // the coach line clickable → /search/coach/<id> (Marin
            // 2026-08-20), same id space StaffCard/CoachHeroBanner navigate
            // with; without id the name stays plain text.
            const coachName =
              team.coach && typeof team.coach === "object"
                ? team.coach.name
                : null;
            const coachId =
              team.coach && typeof team.coach === "object"
                ? team.coach.id
                : null;
            const sheetOpenHere =
              activeSub && isSheetMode && activeSub.side === team.side;
            return (
              <div
                key={team.side}
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {/* team header — crest + name are ONE click target */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minWidth: 0,
                  }}
                >
                  <div
                    role={clubNav ? "link" : undefined}
                    tabIndex={clubNav ? 0 : undefined}
                    onClick={clubNav || undefined}
                    onKeyDown={
                      clubNav
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              clubNav();
                            }
                          }
                        : undefined
                    }
                    className={clubNav ? "hover:bg-[#F8FAFC]" : undefined}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      minWidth: 0,
                      minHeight: 44,
                      cursor: clubNav ? "pointer" : "default",
                      borderRadius: 8,
                      padding: "0 4px",
                      marginLeft: -4,
                    }}
                  >
                    {/* missing crest renders NOTHING (L9) */}
                    {clubLogo && (
                      <img
                        src={clubLogo}
                        alt={clubName || "Club crest"}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 5,
                          flex: "none",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    {clubName && (
                      <div
                        data-no-translate="true"
                        style={{
                          fontSize: m.h2,
                          fontWeight: 700,
                          color: "#0F172A",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {clubName}
                      </div>
                    )}
                  </div>
                  {team.formation && (
                    <div
                      data-no-translate="true"
                      style={{
                        marginLeft: "auto",
                        flex: "none",
                        fontFamily: "ui-monospace,Menlo,monospace",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#334155",
                        background: "#EDF1F6",
                        border: "1px solid #E2E8F0",
                        padding: "3px 8px",
                        borderRadius: 6,
                      }}
                    >
                      {team.formation}
                    </div>
                  )}
                </div>

                {/* pitch + bench (side by side ≥1440, stacked below) */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: m.innerDir,
                    gap: 14,
                    alignItems: "flex-start",
                  }}
                >
                  {team.placeable ? (
                    <Pitch
                      style={{ flex: m.pitchFlex }}
                      dimBottom={!!sheetOpenHere}
                    >
                      {team.slots.map((slot) => (
                        <PlayerNode
                          key={String(slot.player.id)}
                          slot={slot}
                          size={m.nodeSize}
                          nameSize={m.nameSize}
                          incidentSize={m.incidentSize}
                          highlighted={
                            sameId(slot.player.id, highlightPlayerId) ||
                            sameId(
                              slot.player.platform_player_id,
                              highlightPlayerId
                            )
                          }
                          onNavigate={playerNav(slot.player)}
                          onSubOut={(p, el) => {
                            cancelSubClose();
                            openSub(team, p, el);
                          }}
                          onSubOutLeave={scheduleSubClose}
                        />
                      ))}
                    </Pitch>
                  ) : (
                    // name-only lineup (no formation, no coordinates): the
                    // XI as a list — an empty pitch tells the user nothing
                    <div
                      style={{
                        flex: m.pitchFlex,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <BenchList
                        players={team.xi}
                        substitutions={team.subs}
                        highlightPlayerId={highlightPlayerId}
                        title="Starting XI"
                      />
                    </div>
                  )}

                  <div
                    style={{
                      flex: m.benchFlex,
                      minWidth: 0,
                      width: m.innerDir === "row" ? undefined : "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    <BenchList
                      players={team.bench}
                      substitutions={team.subs}
                      highlightPlayerId={highlightPlayerId}
                      coachName={coachName || ""}
                      coachId={coachId}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* unavailable block — per team, only when anyone is listed */}
        {hasUnavailable && (
          <div
            style={{
              borderTop: "1px solid #EDF1F6",
              paddingTop: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#64748B",
                fontWeight: 700,
              }}
            >
              Unavailable
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: m.teamsDir,
                gap: m.teamsGap,
              }}
            >
              {teams.map((team) => (
                <div
                  key={`${team.side}-unavailable`}
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <UnavailableBlock items={team.injuries} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* L12 substitution detail (pitch-node anchors; BenchList carries its
          own card for bench-row anchors) */}
      <SubstitutionCard
        open={!!activeSub}
        rows={activeSub ? activeSub.rows : []}
        anchorEl={activeSub ? activeSub.anchorEl : null}
        forceSheet={isSheetMode}
        onClose={() => setActiveSub(null)}
        onMouseEnter={cancelSubClose}
        onMouseLeave={scheduleSubClose}
      />

      {/* footnote — finest token, only when any rating exists */}
      {hasRatings && (
        <div style={{ fontSize: 11, color: "#94A3B8", paddingTop: 2 }}>
          Ratings supplied by our data partner
        </div>
      )}
    </>
  );
};

export default MatchLineup;
